import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import {
  createClientOffer,
  createClientOfferOption,
  fetchClientOfferOptions,
  updateClientOffer,
} from '@src/api';

export const OFFER_TYPES = [
  { value: 'offer', label: 'Ad Offer' },
  { value: 'lp_offer', label: 'LP Offer' },
  { value: 'financing', label: 'Financing Offer' },
];

export const OPTION_FIELD_MAP = {
  offer: 'ad_offer_option',
  lp_offer: 'lp_offer_option',
  financing: 'financing_offer_option',
};

const EMPTY_OFFER = {
  offer_type: 'offer',
  vertical: '',
  ad_offer_option: '',
  lp_offer_option: '',
  financing_offer_option: '',
  custom_label: '',
  description: '',
  financing_details: '',
  expires_at: '',
};

// Create/edit a single ClientOffer for a known client, with the vertical
// pre-filled from the calling context (e.g. a Creative Knowledge vertical
// card). Mirrors the form on the Client Offers page, including inline
// creation of preset offer options.
export default function ClientOfferEditorDialog({
  open,
  onClose,
  onSaved,
  clientId,
  clientLabel,
  offer,
  verticalOptions = [],
  defaultVertical = '',
}) {
  const [current, setCurrent] = useState(EMPTY_OFFER);
  const [options, setOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [optionDialogOpen, setOptionDialogOpen] = useState(false);
  const [optionSaving, setOptionSaving] = useState(false);
  const [newOptionLabel, setNewOptionLabel] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setCurrent({
      ...EMPTY_OFFER,
      ...(offer || {}),
      vertical: offer?.vertical || defaultVertical || '',
      ad_offer_option: offer?.ad_offer_option || '',
      lp_offer_option: offer?.lp_offer_option || '',
      financing_offer_option: offer?.financing_offer_option || '',
      expires_at: offer?.expires_at || '',
    });
  }, [open, offer, defaultVertical]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetchClientOfferOptions({ offer_type: current.offer_type })
      .then((res) => {
        if (!cancelled) setOptions(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, current.offer_type]);

  const optionField = OPTION_FIELD_MAP[current.offer_type];
  const optionValue = current[optionField] || '';

  const handleSave = async () => {
    if (!optionValue) {
      setError('Select an offer option.');
      return;
    }
    setSaving(true);
    setError('');
    const payload = {
      ...current,
      client: clientId,
      vertical: current.vertical || null,
      custom_label: '',
      expires_at: current.expires_at || null,
    };
    try {
      if (current.id) {
        await updateClientOffer(current.id, payload);
      } else {
        await createClientOffer(payload);
      }
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to save offer');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOption = async () => {
    setOptionSaving(true);
    setError('');
    try {
      const res = await createClientOfferOption({
        offer_type: current.offer_type,
        label: newOptionLabel,
        sort_order: 0,
        active: true,
      });
      const created = res.data;
      setOptionDialogOpen(false);
      setNewOptionLabel('');
      const optionsRes = await fetchClientOfferOptions({ offer_type: current.offer_type });
      setOptions(Array.isArray(optionsRes.data) ? optionsRes.data : []);
      if (created?.id) {
        setCurrent((c) => ({ ...c, [optionField]: created.id }));
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to create option');
    } finally {
      setOptionSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {current.id ? 'Edit Offer' : 'Add Offer'}
          {clientLabel ? (
            <Typography component="div" variant="body2" color="text.secondary">{clientLabel}</Typography>
          ) : null}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <TextField
              select
              label="Offer type"
              value={current.offer_type}
              onChange={(e) => setCurrent((c) => ({ ...c, offer_type: e.target.value }))}
            >
              {OFFER_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Vertical"
              value={current.vertical || ''}
              onChange={(e) => setCurrent((c) => ({ ...c, vertical: e.target.value }))}
            >
              <MenuItem value="">(None)</MenuItem>
              {verticalOptions.map((v) => (
                <MenuItem key={v.id} value={v.id}>
                  {v.vertical}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Offer option"
              value={optionValue}
              onChange={(e) => setCurrent((c) => ({ ...c, [optionField]: e.target.value }))}
              helperText={
                <Stack direction="row" alignItems="center" spacing={1} component="span">
                  <Box component="span">Choose a preset option.</Box>
                  <IconButton
                    size="small"
                    onClick={() => setOptionDialogOpen(true)}
                    aria-label="Add offer option"
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Stack>
              }
            >
              <MenuItem value="">(None)</MenuItem>
              {options.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Description"
              value={current.description || ''}
              onChange={(e) => setCurrent((c) => ({ ...c, description: e.target.value }))}
              multiline
              minRows={2}
            />
            {current.offer_type === 'financing' ? (
              <TextField
                label="Financing details"
                value={current.financing_details || ''}
                onChange={(e) => setCurrent((c) => ({ ...c, financing_details: e.target.value }))}
              />
            ) : null}
            <TextField
              label="Expires at"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={current.expires_at || ''}
              onChange={(e) => setCurrent((c) => ({ ...c, expires_at: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !clientId}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={optionDialogOpen} onClose={() => setOptionDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Offer Option</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Offer type"
              value={OFFER_TYPES.find((t) => t.value === current.offer_type)?.label || current.offer_type}
              disabled
            />
            <TextField
              label="Label"
              value={newOptionLabel}
              onChange={(e) => setNewOptionLabel(e.target.value)}
              required
              autoFocus
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOptionDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveOption} disabled={optionSaving || !newOptionLabel.trim()}>
            {optionSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
